/**
 * ResourceAttachmentPanel — list, upload, and delete attachments on a resource.
 *
 * Props:
 *   resourceType — 'agent' | 'skill' | 'workflow'
 *   resourceId   — UUID of the resource
 *   tenantSlug   — tenant context
 *
 * Two-phase upload:
 *   1. POST /api/v1/attachments/presign  → { uploadUrl, key }
 *   2. PUT <uploadUrl> with the raw file bytes
 *   3. POST /api/v1/attachments/confirm  → AttachmentMeta
 *
 * API_GAP: /api/v1/attachments/presign and /api/v1/attachments/confirm do not yet
 * exist in go-admin-bff. Stubs return mock data until the BFF exposes these endpoints.
 */

import { ApiHttpError, adminBffFetch } from '@avila-roffe/api-clients-admin-bff';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  Spinner,
} from '@avila-roffe/components-web';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Paperclip, Trash2, Upload } from 'lucide-react';
import { useRef } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ResourceAttachment {
  id: string;
  resourceType: 'agent' | 'skill' | 'workflow';
  resourceId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  uploadedBy: string | null;
  createdAt: string;
}

interface PresignResponse {
  uploadUrl: string;
  key: string;
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ResourceAttachmentPanelProps {
  resourceType: 'agent' | 'skill' | 'workflow';
  resourceId: string;
  tenantSlug: string;
  /**
   * When true, hides the standalone "Attachments" heading and renders a compact
   * count + upload button row — suitable for embedding inside a FormSection card.
   */
  embedded?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function toRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

function normalizeAttachment(raw: unknown): ResourceAttachment {
  const row = toRecord(raw);
  return {
    id: String(row.id ?? ''),
    resourceType: (row.resourceType ?? 'skill') as ResourceAttachment['resourceType'],
    resourceId: String(row.resourceId ?? ''),
    fileName: String(row.fileName ?? ''),
    mimeType: String(row.mimeType ?? 'application/octet-stream'),
    sizeBytes: typeof row.sizeBytes === 'number' ? row.sizeBytes : 0,
    uploadedBy: typeof row.uploadedBy === 'string' ? row.uploadedBy : null,
    createdAt: String(row.createdAt ?? ''),
  };
}

// ─── API calls ────────────────────────────────────────────────────────────────

async function fetchAttachments(
  resourceType: string,
  resourceId: string,
): Promise<ResourceAttachment[]> {
  try {
    const response = await adminBffFetch<unknown>(
      `/api/v1/attachments/${resourceType}/${resourceId}`,
      { method: 'GET' },
    );
    const envelope = toRecord(response);
    const data = envelope.data;
    const rows = Array.isArray(data)
      ? data
      : Array.isArray(toRecord(data).data)
        ? (toRecord(data).data as unknown[])
        : [];
    return rows.map(normalizeAttachment);
  } catch (err) {
    // Endpoint not yet implemented — return empty list instead of surfacing a 404
    if (err instanceof ApiHttpError && err.status === 404) return [];
    throw err;
  }
}

async function presignUpload(
  resourceType: string,
  resourceId: string,
  fileName: string,
  mimeType: string,
  tenantSlug: string,
): Promise<PresignResponse> {
  // API_GAP: POST /api/v1/attachments/presign not yet implemented in go-admin-bff
  // TODO: replace stub with real endpoint call when available
  const _response = await Promise.resolve({
    uploadUrl: `https://stub-presign.example.com/${resourceType}/${resourceId}/${fileName}`,
    key: `${resourceType}/${resourceId}/${fileName}`,
  });
  void adminBffFetch;
  void tenantSlug;
  void mimeType;
  return _response;
}

async function confirmUpload(
  key: string,
  resourceType: string,
  resourceId: string,
  fileName: string,
  mimeType: string,
  sizeBytes: number,
  tenantSlug: string,
): Promise<ResourceAttachment> {
  // API_GAP: POST /api/v1/attachments/confirm not yet implemented in go-admin-bff
  // TODO: replace stub with real endpoint call when available
  void key;
  void tenantSlug;
  return Promise.resolve({
    id: `stub-${Date.now()}`,
    resourceType: resourceType as ResourceAttachment['resourceType'],
    resourceId,
    fileName,
    mimeType,
    sizeBytes,
    uploadedBy: null,
    createdAt: new Date().toISOString(),
  });
}

async function removeAttachment(id: string): Promise<void> {
  // API_GAP: DELETE /api/v1/attachments/{id} not yet implemented in go-admin-bff
  // TODO: replace stub with real endpoint call when available
  await adminBffFetch<unknown>(`/api/v1/attachments/${id}`, { method: 'DELETE' });
}

// ─── Two-phase upload ─────────────────────────────────────────────────────────

async function twoPhaseUpload(
  file: File,
  resourceType: string,
  resourceId: string,
  tenantSlug: string,
): Promise<ResourceAttachment> {
  const { uploadUrl, key } = await presignUpload(
    resourceType,
    resourceId,
    file.name,
    file.type,
    tenantSlug,
  );

  // API_GAP: PUT to presigned URL — skipped in stub mode since uploadUrl is fake
  // TODO: enable when presign endpoint is real:
  // await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
  void uploadUrl;

  return confirmUpload(key, resourceType, resourceId, file.name, file.type, file.size, tenantSlug);
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ResourceAttachmentPanel({
  resourceType,
  resourceId,
  tenantSlug,
  embedded = false,
}: ResourceAttachmentPanelProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const queryKey = ['attachments', resourceType, resourceId];

  const {
    data: attachments = [],
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: () => fetchAttachments(resourceType, resourceId),
    enabled: Boolean(resourceId),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => twoPhaseUpload(file, resourceType, resourceId, tenantSlug),
    onSuccess: (created) => {
      queryClient.setQueryData<ResourceAttachment[]>(queryKey, (prev) =>
        prev ? [...prev, created] : [created],
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => removeAttachment(id),
    onSuccess: (_data, deletedId) => {
      queryClient.setQueryData<ResourceAttachment[]>(queryKey, (prev) =>
        prev ? prev.filter((a) => a.id !== deletedId) : [],
      );
    },
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    uploadMutation.mutate(file);
  }

  const uploadButton = (
    <Button
      type="button"
      size="sm"
      variant="secondary"
      appearance="ghost"
      className="size-7 p-0 shrink-0"
      onClick={() => fileRef.current?.click()}
      disabled={uploadMutation.isPending}
      aria-label="Upload attachment"
    >
      {uploadMutation.isPending ? <Spinner className="size-3" /> : <Upload className="size-3" />}
    </Button>
  );

  return (
    <div className={embedded ? 'space-y-3' : 'space-y-3 pt-4'}>
      {/* Header row */}
      {embedded ? (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            {attachments.length} attachment{attachments.length !== 1 ? 's' : ''}
          </p>
          {uploadButton}
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Paperclip className="size-4" />
            Attachments
            {attachments.length > 0 && (
              <Badge variant="secondary" appearance="soft" className="text-xs">
                {attachments.length}
              </Badge>
            )}
          </div>
          {uploadButton}
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        className="hidden"
        onChange={handleFileChange}
        aria-label="Select file to upload"
      />

      {/* Upload error */}
      {uploadMutation.isError && (
        <p className="text-xs text-destructive">
          {uploadMutation.error instanceof Error ? uploadMutation.error.message : 'Upload failed'}
        </p>
      )}

      {/* Delete error */}
      {deleteMutation.isError && (
        <p className="text-xs text-destructive">
          {deleteMutation.error instanceof Error ? deleteMutation.error.message : 'Delete failed'}
        </p>
      )}

      {/* Fetch error */}
      {error && (
        <p className="text-xs text-destructive">
          {error instanceof Error ? error.message : 'Failed to load attachments'}
        </p>
      )}

      {/* List */}
      {isLoading ? (
        <Spinner />
      ) : attachments.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No attachments yet. Upload reference docs, scripts, or config files.
        </p>
      ) : (
        <ul className="space-y-1">
          {attachments.map((a) => (
            <li
              key={a.id}
              className="flex items-center gap-2 rounded-md border px-2 py-1.5 text-xs"
            >
              <FileText className="size-3 shrink-0 text-muted-foreground" />
              <span className="flex-1 truncate font-mono" title={a.fileName}>
                {a.fileName}
              </span>
              <span className="shrink-0 text-muted-foreground">{formatBytes(a.sizeBytes)}</span>
              <span className="shrink-0 text-muted-foreground">
                {new Date(a.createdAt).toLocaleDateString()}
              </span>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    appearance="ghost"
                    className="size-5 p-0 opacity-60 hover:opacity-100"
                    disabled={deleteMutation.isPending}
                    aria-label={`Delete ${a.fileName}`}
                  >
                    <Trash2 className="size-3" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete attachment?</AlertDialogTitle>
                    <AlertDialogDescription>
                      <span className="font-mono">{a.fileName}</span> will be permanently removed.
                      This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => deleteMutation.mutate(a.id)}>
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
